import { type WatchQueryFetchPolicy } from '@apollo/client';
import { GraphQLError } from 'graphql';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { tokenizeCard } from 'components/add-payment-method-modal/tokenize-card';
import { IEncryptionResult as IOrbitalEncryptionResult } from 'components/payments/integrations/orbital/components/encryption/types';
import { IEncryptionResult as IWorldpayEncryptionResult } from 'components/payments/integrations/worldpay/components/encryption/types';
import {
  IAddAccountInput,
  IAddOrbitalAccountInput,
  IPrepaidsMergeInput,
  UserAccountsDocument,
  useDeleteAccountMutation,
  usePrepaidsMergeMutation,
  useUserAccountsLazyQuery,
} from 'generated/rbi-graphql';
import { useConfigValue } from 'hooks/configs/use-config-value';
import { usePrepaidsReload } from 'hooks/prepaid';
import { getNonce } from 'remote/api/first-data';
import { HttpErrorCodes } from 'remote/constants';
import { useAmplitudeContext } from 'state/amplitude';
import useApplePay from 'state/apple-pay/hooks/use-apple-pay';
import useCanUseApplePay from 'state/apple-pay/hooks/use-can-use-apple-pay';
import { CustomEventNames, EventTypes, useCRMEventsContext } from 'state/crm-events';
import useGooglePay from 'state/google-pay/hooks/use-google-pay';
import { useLocale } from 'state/intl';
import { useStoreContext } from 'state/store';
import { storePrepaidCard } from 'utils/encryption';
import { sanitizeAlphanumeric, sanitizeNumber } from 'utils/form';
import { ISOsToRegions } from 'utils/form/constants';
import { useFlag } from 'utils/launchdarkly';
import {
  PaymentFieldVariations,
  defaultPaymentFieldVariation,
} from 'utils/launchdarkly/variations';
import logger from 'utils/logger';
import { IPaymentPayload, IPaymentState, splitExpiry } from 'utils/payment';
import { PaymentProcessor } from 'utils/rbi-common';

import { CASH_ACCOUNT_IDENTIFIER } from '../constants';
import { IAddPaymentMethodOptions, IGiftCard, IPaymentMethod, IReloadPrepaidCard } from '../types';

import { getPaymentMethodsState } from './getPaymentMethodsState';
import { IInitPaymentMethods, IPayment } from './types';

const cashAccount: IPaymentMethod = {
  cash: true,
  fdAccountId: CASH_ACCOUNT_IDENTIFIER,
  accountIdentifier: CASH_ACCOUNT_IDENTIFIER,
  chaseProfileId: null,
  credit: null,
  prepaid: null,
  paypal: null,
};

const usePayment = ({
  getEncryptionDetailsMutation,
  openErrorDialog,
  user,
  updateUserInfo,
  addCreditAccountMutation,
  userLoading,
}: IPayment) => {
  const { formatMessage } = useIntl();
  const { feCountryCode } = useLocale();
  const [paymentMethods, setPaymentMethods] = useState<IPaymentMethod[]>([]);
  const [hasGetPaymentMethodsError, setHasGetPaymentMethodsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const canUseApplePay = useCanUseApplePay();
  const { applePayCardDetails } = useApplePay({});
  const { canUseGooglePay, googlePayCardDetails } = useGooglePay();
  const { store } = useStoreContext();
  const enableCashPayment = useFlag('enable-cash-payment');
  const enablePayPal = useFlag('enable-paypal-payments');
  const onlySendPostalCode = useFlag('send-postal-code-only-for-first-data-payment');
  const [fireDeleteAccountMutation] = useDeleteAccountMutation({
    awaitRefetchQueries: true,
    refetchQueries: [{ query: UserAccountsDocument, variables: { feCountryCode } }],
  });
  const [firePrepaidMergeMutation] = usePrepaidsMergeMutation({
    refetchQueries: [{ query: UserAccountsDocument, variables: { feCountryCode } }],
  });

  const fdAccessTokenFirstRequest = useRef<string | null>(null);

  const publicKey = useConfigValue({ key: 'tokenEx', defaultValue: {} }).publicKey;
  const urlsConfig = useConfigValue({ key: 'urls', defaultValue: {} });
  const fdUrl = useMemo(
    () => urlsConfig.firstData || urlsConfig.firstDataTh,
    [urlsConfig.firstData, urlsConfig.firstDataTh]
  );

  const [
    fetchUserAccounts,
    { data: userAccountsData, loading: accountsLoading, error: userAccountsError },
  ] = useUserAccountsLazyQuery({
    variables: { feCountryCode },
    // Apollo client loading state gets stuck: https://github.com/apollographql/react-apollo/issues/3425
    // Temporary fix while we wait for a stable 3.0.0 version
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    setHasGetPaymentMethodsError(Boolean(userAccountsError));
  }, [userAccountsError]);

  const { cognitoId, details } = user || {};
  const { defaultPaymentAccountId: defaultPrepaidPaymentMethodId } = details || {};

  // note: defaultFdAccountId is deprecated. use defaultAccountIdentifier if present
  const defaultAccountIdentifier = details?.defaultAccountIdentifier || details?.defaultFdAccountId;

  const [checkoutPaymentMethodId, setCheckoutPaymentMethodId] = useState('');
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState('');
  const [defaultReloadPaymentMethodId, setDefaultReloadPaymentMethodId] = useState('');
  const [prepaidReloadPaymentMethodId, setPrepaidReloadPaymentMethodId] = useState('');
  const [paymentMethodHasBeenInit, setPaymentMethodHasBeenInit] = useState(false);
  const { updateAmplitudeUserAttributes } = useAmplitudeContext();

  const saveNewPaymentMethodEvent = (success: boolean, context: Record<string, any> = {}) => {
    if (success) {
      logger.info({
        message: 'Payment method added',
        context,
      });
    } else {
      logger.error({
        message: 'Error adding payment method',
        context,
      });
    }
  };

  const logUserPaymentIdentity = useCallback(
    (accountIdentifier: string) => {
      if (!user || !details) {
        return;
      }
      const selectedMethod = paymentMethods.find(
        method =>
          method.accountIdentifier === accountIdentifier || method.fdAccountId === accountIdentifier
      );
      if (!selectedMethod) {
        return;
      }
      let ccToken;
      if (selectedMethod.credit) {
        ccToken = selectedMethod.credit.panToken;
      } else if (selectedMethod.prepaid) {
        ccToken = selectedMethod.prepaid.cardNumber;
      }
      if (ccToken) {
        updateAmplitudeUserAttributes({
          ccToken,
        });
      }
    },
    [user, details, paymentMethods, updateAmplitudeUserAttributes]
  );

  const setAndLogCheckoutPaymentMethodId = useCallback(
    (fdAccountId: string) => {
      logUserPaymentIdentity(fdAccountId);
      setCheckoutPaymentMethodId(fdAccountId);
    },
    [logUserPaymentIdentity, setCheckoutPaymentMethodId]
  );

  const setAndLogDefaultReloadPaymentMethodId = useCallback(
    (accountId: string) => {
      setPrepaidReloadPaymentMethodId(accountId);
      setDefaultReloadPaymentMethodId(accountId);
      if (user) {
        updateUserInfo({
          ...user.details,
          defaultReloadPaymentMethodId: accountId,
        });
      }
    },
    [user, updateUserInfo]
  );

  const updatePaymentMethodsState = useCallback(
    (
      validDefaultPaymentMethodId: string,
      validDefaultReloadPaymentMethodId: string,
      shouldMuteUpdateUserInfoErrors?: boolean
    ) => {
      /*
      call updateMe query only if user has a invalid defaultAccountIdentifier
      or if it's different than the validDefaultPaymentMethodId returned by the getPaymentMethodsState hook
      */
      const shouldUpdateUser =
        !user?.details.defaultAccountIdentifier ||
        validDefaultPaymentMethodId !== user?.details.defaultAccountIdentifier;

      setDefaultPaymentMethodId(validDefaultPaymentMethodId);
      setCheckoutPaymentMethodId(validDefaultPaymentMethodId);
      setPrepaidReloadPaymentMethodId(validDefaultReloadPaymentMethodId);
      setDefaultReloadPaymentMethodId(validDefaultReloadPaymentMethodId);

      if (user && shouldUpdateUser) {
        // Update the checkout and reload payment method when adding a new CC
        updateUserInfo(
          {
            ...user.details,
            defaultCheckoutPaymentMethodId: validDefaultPaymentMethodId,
            defaultReloadPaymentMethodId: validDefaultReloadPaymentMethodId,
          },
          {
            shouldMuteUserInfoErrors: shouldMuteUpdateUserInfoErrors,
          }
        );
      }
    },
    [updateUserInfo, user]
  );

  const setAndLogDefaultPaymentMethodId = useCallback(
    (accountId: string, isAddingNewCc?: boolean) => {
      logUserPaymentIdentity(accountId);
      setDefaultPaymentMethodId(accountId);
      // Set the checkout payment method when the user change its default one
      setCheckoutPaymentMethodId(accountId);

      if (user) {
        // Update the checkout and reload payment method when adding a new CC
        updateUserInfo({
          ...user.details,
          defaultCheckoutPaymentMethodId: accountId,
          ...(isAddingNewCc ? { defaultReloadPaymentMethodId: accountId } : {}),
        });
      }
    },
    [logUserPaymentIdentity, setDefaultPaymentMethodId, user, updateUserInfo]
  );

  const getEncryptionDetails = useCallback(async () => {
    const encryptionDetailsResponse = await getEncryptionDetailsMutation();

    if (!encryptionDetailsResponse.data) {
      throw new Error('Missing encryption details');
    }

    return encryptionDetailsResponse.data.encryptionDetails;
  }, [getEncryptionDetailsMutation]);

  const clearPaymentMethods = useCallback(() => {
    setPaymentMethods([]);
    setHasGetPaymentMethodsError(false);
    setLoading(false);
  }, []);

  const getPaymentMethods = useCallback(
    async (options?: { fetchPolicy?: WatchQueryFetchPolicy }) => {
    try {
      if (cognitoId) {
        setLoading(true);
        const { data } = await fetchUserAccounts({
          variables: { feCountryCode },
          fetchPolicy: options?.fetchPolicy ?? 'cache-first',
        });
        return data?.userAccounts?.accounts ?? [];
      }
      return [];
    } catch (error) {
      logger.error({
        error,
        message: 'Error getting payment methods',
      });
      clearPaymentMethods();
      setHasGetPaymentMethodsError(true);
      openErrorDialog({
        message: formatMessage({ id: 'paymentLoadingError' }),
        modalAppearanceEventMessage: 'Error: Fetching Payment Methods Failure',
        // @ts-expect-error TS(2322) FIXME: Type 'unknown' is not assignable to type 'Error | ... Remove this comment to see the full error message
        error,
      });
      return [];
    } finally {
      setLoading(false);
    }
  },
    [
      fetchUserAccounts,
      feCountryCode,
      clearPaymentMethods,
      openErrorDialog,
      formatMessage,
      cognitoId,
    ]
  );

  /**
   * set up a bunch of state related to a customer's payment accounts and methods
   * @param {IPaymentMethod[]} accounts - list of ways a customer could pay (prepaid, credit card, mobile wallets etc)
   * @param {string} paymentMethodId - attempt to use this as the default payment method
   * @param {string} prepaidPaymentMethodId - attempt to use this as the default prepaid payment method
   */
  const initPaymentMethods = useCallback(
    (
      { accounts, paymentMethodId, prepaidPaymentMethodId }: IInitPaymentMethods,
      shouldMuteUpdateUserInfoErrors?: boolean
    ) => {
      // Get valid payment methods
      const {
        availablePaymentMethodList,
        validDefaultPaymentMethodId,
        validDefaultReloadPaymentMethodId,
      } = getPaymentMethodsState({
        paymentMethodId,
        prepaidPaymentMethodId,
        accounts,
        canUseApplePay,
        enableCashPayment,
        enablePayPal,
        applePayCardDetails,
        canUseGooglePay,
        googlePayCardDetails,
        paymentMethodHasBeenInit,
      });

      // Setup the payment methods into a valid state
      setPaymentMethods(availablePaymentMethodList);
      updatePaymentMethodsState(
        validDefaultPaymentMethodId,
        validDefaultReloadPaymentMethodId,
        shouldMuteUpdateUserInfoErrors
      );
    },
    [
      applePayCardDetails,
      canUseApplePay,
      enableCashPayment,
      enablePayPal,
      canUseGooglePay,
      googlePayCardDetails,
      paymentMethodHasBeenInit,
      updatePaymentMethodsState,
    ]
  );

  useEffect(() => {
    if (!user) {
      clearPaymentMethods();
      return;
    }
    if (userAccountsData) {
      const accounts: IPaymentMethod[] =
        (userAccountsData.userAccounts &&
          userAccountsData.userAccounts.accounts &&
          userAccountsData.userAccounts.accounts.filter<IPaymentMethod>(
            (account): account is IPaymentMethod =>
              !!account && (!!account.fdAccountId || !!account.accountIdentifier)
          )) ||
        [];

      setHasGetPaymentMethodsError(false);

      if (accounts.length) {
        storePrepaidCard({ accounts });
      }
      setLoading(false);
    }
  }, [clearPaymentMethods, user, userAccountsData]);

  const { reloadPrepaidsCardMutation, loading: prepaidLoading } = usePrepaidsReload({
    openErrorDialog,
  });

  const getPrepaidPaymentMethod = useCallback(() => {
    return paymentMethods.find(method => Boolean(method.prepaid)) || null;
  }, [paymentMethods]);

  const getBalanceFromPaymentMethods = (prepaidPaymentMethod: IPaymentMethod) => {
    if (prepaidPaymentMethod && prepaidPaymentMethod.prepaid) {
      if (prepaidPaymentMethod.prepaid.feFormattedCurrentBalance) {
        return prepaidPaymentMethod.prepaid.feFormattedCurrentBalance;
      } else if (prepaidPaymentMethod.prepaid.currentBalance) {
        return prepaidPaymentMethod.prepaid.currentBalance;
      }
    }
    return 0;
  };

  const getBalanceFromGiftCard = (giftCard: IGiftCard) => {
    return giftCard?.currentBalance ?? 0;
  };

  const getPrepaidCardNumber = useCallback(() => {
    const prepaidPaymentMethod = getPrepaidPaymentMethod();

    if (prepaidPaymentMethod && prepaidPaymentMethod.prepaid) {
      // add spaces between every 4 characters in the card number
      return (prepaidPaymentMethod.prepaid.cardNumber.match(/.{1,4}/g) || []).join(' ');
    }
    return null;
  }, [getPrepaidPaymentMethod]);

  const deletePaymentMethod = useCallback(
    async (accountIdentifier: string) => {
      try {
        setIsDeleting(true);
        const { data } = await fireDeleteAccountMutation({
          variables: { input: { accountIdentifier } },
        });
        if (data?.deleteAccount) {
          // Strip the paymentMethods list from the deleted CC
          const updatedPaymentMethods = paymentMethods.filter(
            method => method.accountIdentifier !== accountIdentifier
          );
          // If the default payment method is the deleted one, reset the payment method using the 1st in the updated array
          const paymentMethodIdAfterDelete =
            defaultAccountIdentifier !== accountIdentifier
              ? defaultAccountIdentifier
              : updatedPaymentMethods[0]?.accountIdentifier;

          // Re-init the payment methods state to make sure we're in a fully working state after deleting a CC
          initPaymentMethods({
            accounts: updatedPaymentMethods,
            paymentMethodId: paymentMethodIdAfterDelete || '',
            prepaidPaymentMethodId: paymentMethodIdAfterDelete || '',
          });
        }
      } catch (error) {
        logger.error({ error, message: 'Error deleting payment method' });
        openErrorDialog({
          message: formatMessage({ id: 'paymentDeletingError' }),
          modalAppearanceEventMessage: 'Error: Deleting Payment Method Failure',
          // @ts-expect-error TS(2322) FIXME: Type 'unknown' is not assignable to type 'Error | ... Remove this comment to see the full error message
          error,
        });
      } finally {
        setIsDeleting(false);
      }
    },
    [
      fireDeleteAccountMutation,
      paymentMethods,
      defaultAccountIdentifier,
      initPaymentMethods,
      openErrorDialog,
      formatMessage,
    ]
  );

  const paymentProcessor = userAccountsData?.userAccounts?.paymentProcessor;
  const isFirstData = paymentProcessor === PaymentProcessor.FIRSTDATA;
  const isOrbital = paymentProcessor === PaymentProcessor.ORBITAL;
  const isWorldpay = paymentProcessor === PaymentProcessor.WORLDPAY;

  const transformPaymentValues = useCallback(
    ({
      paymentValues,
      paymentFieldVariations = defaultPaymentFieldVariation,
    }: {
      paymentValues: IPaymentState;
      paymentFieldVariations?: PaymentFieldVariations;
    }): IPaymentPayload => {
      let expiryDate: null | { month: string; year: string } = null;
      let { cardNumber } = paymentValues;

      cardNumber = sanitizeNumber(paymentValues.cardNumber);
      const { expiryMonth, expiryYear } = splitExpiry(paymentValues.expiry ?? '');
      expiryDate = paymentValues.expiry
        ? {
            month: expiryMonth,
            year: expiryYear,
          }
        : null;
      const country = paymentValues.billingCountry
        ? ISOsToRegions[paymentValues.billingCountry]
        : null;
      const postalCode = sanitizeAlphanumeric(paymentValues.billingZip);
      return {
        billingAddress: {
          country: paymentFieldVariations.country ? country : null,
          postalCode: paymentFieldVariations.zip ? postalCode : '',
          unitNumber: paymentFieldVariations.addressLine2 ? paymentValues.billingApt : null,
          locality: paymentFieldVariations.city ? paymentValues.billingCity : null,
          region: paymentFieldVariations.state ? paymentValues.billingState : null,
          streetAddress: paymentFieldVariations.addressLine1
            ? paymentValues.billingStreetAddress
            : null,
        },
        cardNumber,
        cardType: paymentValues.cardType,
        expiryDate,
        nameOnCard: paymentValues.nameOnCard,
        securityCode: paymentValues.cvv,
      };
    },
    []
  );

  const [encryptionResult, setEncryptionResult] = useState<
    IOrbitalEncryptionResult | IWorldpayEncryptionResult | undefined
  >();

  const { logRBIEvent } = useCRMEventsContext();

  const addPaymentMethod = useCallback(
    async (
      paymentValues: IPaymentPayload,
      { skipErrorDialogOnError = false }: IAddPaymentMethodOptions = {},
      encryptionResult?: IOrbitalEncryptionResult | IWorldpayEncryptionResult
    ) => {
      const { billingAddress, cardNumber, nameOnCard: fullName } = paymentValues;
      const { accountToDelete, ...cleanedPaymentValues } = paymentValues;

      logRBIEvent({
        name: CustomEventNames.SAVE_NEW_PAYMENT_METHOD,
        type: EventTypes.UserContent,
        attributes: {
          paymentCardType: paymentValues.cardType,
        },
      });

      if (!loading) {
        setLoading(true);
      }

      try {
        // If we're re-vaulting, delete the payment method before adding the new one

        let addAccount;
        let chaseProfileId;
        let accountIdentifier;

        if (isOrbital) {
          if (!encryptionResult) {
            throw new Error(
              'Orbital encryption results encryption results not available for add payment method'
            );
          }

          // If we're re-vaulting, delete the payment method before adding the new one
          if (accountToDelete) {
            await deletePaymentMethod(accountToDelete);
          }

          const orbitalEncryptionResult = encryptionResult;

          if (!orbitalEncryptionResult || !('cryptCard' in orbitalEncryptionResult)) {
            throw new Error('Orbital Encryption Failure');
          }

          const orbitalInput: IAddOrbitalAccountInput = {
            encryptedCardNum: orbitalEncryptionResult.cryptCard,
            encryptedCvv: orbitalEncryptionResult.cryptCvv,
            expiryMonth: paymentValues.expiryDate?.month ?? '',
            expiryYear: paymentValues.expiryDate?.year ?? '',
            pieFormat: orbitalEncryptionResult.pieFormat,
            pieMode: orbitalEncryptionResult.mode,
            piePhaseId: String(orbitalEncryptionResult.phase),
            pieIntegrityCheck: orbitalEncryptionResult.integrityCheck,
            pieSubscriberId: orbitalEncryptionResult.subscriberId,
            pieKeyID: orbitalEncryptionResult.keyId,
            cardBrand: paymentValues.cardType.toLowerCase(),
            bin: cardNumber.substring(0, 6),
          };
          const input = {
            billingAddress,
            orbitalInput,
            fullName: paymentValues.nameOnCard,
            storeId: store.number,
          };

          const addAccountResponse = await addCreditAccountMutation({
            variables: {
              input,
            },
          });

          // Assertion of minimum data for add account.
          if (!addAccountResponse.data?.addCreditAccount.accountIdentifier) {
            throw new Error('Add Account Failure');
          }

          addAccount = addAccountResponse.data.addCreditAccount;
          accountIdentifier = addAccount.accountIdentifier ?? '';
        } else if (isWorldpay) {
          if (!encryptionResult) {
            throw new Error(
              'Worldpay encryption results encryption results not available for add payment method'
            );
          }

          // If we're re-vaulting, delete the payment method before adding the new one
          if (accountToDelete) {
            await deletePaymentMethod(accountToDelete);
          }

          const worldpayEncryptionResult = encryptionResult;

          if (!worldpayEncryptionResult || !('lowValueToken' in worldpayEncryptionResult)) {
            throw new Error('Orbital Encryption Failure');
          }

          const worldpayInput = {
            lowValueToken: worldpayEncryptionResult.lowValueToken,
            bin: paymentValues.cardNumber.substring(0, 6),
            last4: paymentValues.cardNumber.slice(-4),
            cardBrand: paymentValues.cardType.toLowerCase(),
            expiryMonth: paymentValues.expiryDate?.month ?? '',
            expiryYear: paymentValues.expiryDate?.year ?? '',
          };

          const input = {
            billingAddress,
            fullName: paymentValues.nameOnCard,
            worldpayInput,
            storeId: store.number,
          };

          const addAccountResponse = await addCreditAccountMutation({
            variables: {
              input,
            },
          });

          // Assertion of minimum data for add account.
          if (!addAccountResponse.data?.addCreditAccount.accountIdentifier) {
            throw new Error('Add Account Failure');
          }

          addAccount = addAccountResponse.data.addCreditAccount;
          accountIdentifier = addAccount.accountIdentifier ?? '';
        } else {
          // Handles adding a payment method when the payment processor is First Data
          const ccBin = cardNumber.slice(0, 6);
          chaseProfileId = await tokenizeCard(paymentValues, user, publicKey, '');

          let firstDataInput: IAddAccountInput = { ccBin: '', fdAccessToken: '', fdNonce: '' };
          let addAccountResponse: any = {};

          /**
           * METHOD FLOW
           * this means that we are in a frictionless flow and we need to resume
           * the add credit card account with some previous data we take in the
           * last execution of the current flow.
           * CHALLENGE FLOW
           * Little bit different to METHOD FLOW where it require a new parameter
           * called challengeResponse and transactionId too.
           **/
          // If we're re-vaulting, delete the payment method before adding the new one
          if (accountToDelete) {
            await deletePaymentMethod(accountToDelete);
          }

          const {
            fdPublicKey,
            fdApiKey,
            fdAccessToken,
            fdCustomerId, // Necessary second request
            algorithm,
          } = await getEncryptionDetails();

          if (!fdAccessTokenFirstRequest.current) {
            fdAccessTokenFirstRequest.current = fdAccessToken;
          }

          const nonceResponse = await getNonce(
            cleanedPaymentValues,
            fdPublicKey,
            fdApiKey,
            fdAccessToken,
            // @ts-expect-error TS(2345) FIXME: Argument of type 'string | null | undefined' is no... Remove this comment to see the full error message
            fdCustomerId,
            fdUrl,
            onlySendPostalCode,
            algorithm
          );
          const { token } = await nonceResponse.json();
          const fdNonce = token.tokenId;

          firstDataInput = {
            fdAccessToken,
            fdNonce,
            ccBin,
            chaseProfileId,
          };

          try {
            addAccountResponse = await addCreditAccountMutation({
              variables: {
                input: {
                  billingAddress,
                  firstDataInput,
                  fullName,
                },
              },
            });
          } catch (error) {
            throw error;
          }

          // Assertion of minimun data for add account.
          if (
            !addAccountResponse.data?.addCreditAccount.accountIdentifier &&
            !addAccountResponse.data?.addCreditAccount.fdAccountId
          ) {
            throw new Error('Add Account Failure');
          }

          addAccount = addAccountResponse.data.addCreditAccount;
          accountIdentifier = addAccount.accountIdentifier ?? addAccount.fdAccountId ?? '';
        }

        saveNewPaymentMethodEvent(true, { chaseProfileId, accountIdentifier });

        // Re-init the payment methods state to make sure we're in a fully working state after adding a CC
        initPaymentMethods({
          accounts: [...paymentMethods, addAccount as IPaymentMethod],
          paymentMethodId: accountIdentifier,
          prepaidPaymentMethodId: accountIdentifier,
        });

        // When a user adds a CC, set the Checkout and Reload Default payment method to the newly added one
        await setAndLogDefaultPaymentMethodId(accountIdentifier, true);

        return accountIdentifier;
      } catch (error) {
        logger.error({
          error,
          message: 'Error adding payment method',
        });
        saveNewPaymentMethodEvent(false, {
          chaseProfileId: undefined,
          accountIdentifier: undefined,
          // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
          message: error.message,
        });
        if (!skipErrorDialogOnError) {
          const statusCode:
            | number
            // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
            | undefined = error.graphQLErrors?.find((graphQLError: GraphQLError) =>
            Number.isInteger(graphQLError?.extensions?.statusCode)
          )?.extensions?.statusCode;
          openErrorDialog({
            message:
              statusCode === HttpErrorCodes.TooManyRequests
                ? formatMessage({ id: 'tooManyAddAccountAttempts' })
                : formatMessage({ id: 'paymentAddingError' }),
            modalAppearanceEventMessage: 'Error: Adding Payment Method Failure',
            // @ts-expect-error TS(2322) FIXME: Type 'unknown' is not assignable to type 'Error | ... Remove this comment to see the full error message
            error,
          });
        }
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [
      addCreditAccountMutation,
      deletePaymentMethod,
      fdUrl,
      formatMessage,
      getEncryptionDetails,
      initPaymentMethods,
      isOrbital,
      isWorldpay,
      loading,
      onlySendPostalCode,
      openErrorDialog,
      paymentMethods,
      publicKey,
      setAndLogDefaultPaymentMethodId,
      user,
    ]
  );

  const reloadPrepaidCard = useCallback(
    async (reloadInfo: IReloadPrepaidCard) => {
      try {
        const { data } = await reloadPrepaidsCardMutation(reloadInfo);

        return data?.prepaidsReload?.currentBalance;
      } catch (error) {
        logger.error({
          error,
          message: 'Error reloading prepaid card',
        });
        return undefined;
      }
    },
    [reloadPrepaidsCardMutation]
  );

  const mergePrepaidCardBalances = async ({
    destinationFdAccountId,
    sourceCardNumber,
    sourcePin,
  }: IPrepaidsMergeInput) => {
    if (!loading) {
      setLoading(true);
    }

    try {
      const { data: wrapper } = await firePrepaidMergeMutation({
        variables: {
          input: {
            destinationFdAccountId,
            sourceCardNumber: sourceCardNumber.replace(/\s/g, ''),
            sourcePin,
          },
        },
      });

      const data = wrapper!.prepaidsMerge;

      await getPaymentMethods();

      // Set the newly added GC, or the desitination GC as the checkout payment method.
      setCheckoutPaymentMethodId(data.fdAccountId || destinationFdAccountId || '');

      return data;
    } finally {
      setLoading(false);
    }
  };

  const checkoutPaymentMethod = paymentMethods.find(
    ({ accountIdentifier, fdAccountId }) =>
      accountIdentifier === checkoutPaymentMethodId || fdAccountId === checkoutPaymentMethodId
  );

  useEffect(
    () => {
      // Initialize the payment method when the app loads
      if (userAccountsData?.userAccounts?.accounts) {
        // First time running, set the paymentMethodHasBeenInit to true
        // This prevent re-init when the user's accounts change
        const { accounts } = userAccountsData.userAccounts;

        /**
         * Unlike Apple Pay and Google Pay, the cash method can be a default payment method
         * so add it before `initPaymentMethods` runs so it can be sorted & stored as the default method in FE state
         */
        const accountsMaybeWithCash = enableCashPayment ? [...accounts, cashAccount] : accounts;

        if (!paymentMethodHasBeenInit) {
          setPaymentMethodHasBeenInit(true);
          // When we have the user's payment method list, initialize the payment methods state
          initPaymentMethods(
            {
              accounts: accountsMaybeWithCash as IPaymentMethod[],
              paymentMethodId: defaultAccountIdentifier || '',
              prepaidPaymentMethodId: defaultPrepaidPaymentMethodId || '',
            },
            true
          );
        } else {
          /**
           * If the payment methods has been initialized, prefer the profile default when it matches an account
           * (e.g. after vaulting a card at checkout, GetMe returns the new defaultAccountIdentifier).
           * Otherwise use checkoutPaymentMethodId to keep state stable. Cash as default is a special case.
           */
          const canUseCashAsDefaultPaymentMethod =
            defaultAccountIdentifier === CASH_ACCOUNT_IDENTIFIER && enableCashPayment;

          const defaultMatchesAnAccount =
            defaultAccountIdentifier &&
            accounts.some((acc) =>
              acc &&
              (acc.accountIdentifier === defaultAccountIdentifier ||
                acc.fdAccountId === defaultAccountIdentifier)
            );

          const paymentMethodIdToUse = canUseCashAsDefaultPaymentMethod
            ? defaultAccountIdentifier || ''
            : defaultMatchesAnAccount
              ? defaultAccountIdentifier || ''
              : checkoutPaymentMethodId;

          initPaymentMethods(
            {
              accounts: accountsMaybeWithCash as IPaymentMethod[],
              paymentMethodId: paymentMethodIdToUse,
              prepaidPaymentMethodId: prepaidReloadPaymentMethodId,
            },
            true
          );
        }
      }
    },
    /* eslint-disable react-hooks/exhaustive-deps */
    [
      cognitoId,
      enableCashPayment,
      defaultAccountIdentifier,
      userAccountsData?.userAccounts?.accounts,
    ]
    /* eslint-enable react-hooks/exhaustive-deps */
  );

  return {
    loading: loading || accountsLoading || prepaidLoading || isDeleting || userLoading,
    paymentMethods,
    getPaymentMethods,
    getBalanceFromPaymentMethods,
    getBalanceFromGiftCard,
    getPrepaidPaymentMethod,
    getPrepaidCardNumber,
    getEncryptionDetails,
    checkoutPaymentMethod,
    checkoutPaymentMethodId,
    setCheckoutPaymentMethodId: setAndLogCheckoutPaymentMethodId,
    defaultPaymentMethodId,
    defaultReloadPaymentMethodId,
    setDefaultPaymentMethodId: setAndLogDefaultPaymentMethodId,
    setDefaultReloadPaymentMethodId: setAndLogDefaultReloadPaymentMethodId,
    setPrepaidReloadPaymentMethodId,
    prepaidReloadPaymentMethodId,
    addPaymentMethod,
    deletePaymentMethod,
    reloadPrepaidCard,
    mergePrepaidCardBalances,
    hasGetPaymentMethodsError,
    canUseApplePay,
    canUseGooglePay,
    paymentProcessor,
    isFirstData,
    isOrbital,
    isWorldpay,
    transformPaymentValues,
    encryptionResult,
    setEncryptionResult,
  };
};

export default usePayment;
